import classNames from 'classnames';
import React, { useContext, useRef } from 'react';

import {
  EditorContext,
  EditorQueryContext,
  ModalServiceContext,
  RicosContext,
  ToolbarContext,
} from './../../../ricos-context';
import type { ModalService } from './../../../ricos-modals';
import { getIsModalOpen, useModalState } from './../../../ricos-modals/modal-service-provider';
import type { AddPluginMenuConfig, LayoutTypes, Placement } from './../../../ricos-types';
import { PLUGIN_MENU_HORIZONTAL_MODAL_ID, PLUGIN_MENU_MODAL_ID } from './../../../ricos-types';

import { useSelectionToPosition } from './EditorSelectionToPosition';
import PlusButton from './PlusButton';
import styles from './styles/floating-add-plugin-menu.rtlignore-scss';

interface Props {
  addPluginMenuConfig?: AddPluginMenuConfig;
  supportedButtonIds?: string[];
  onClick?: () => void;
  isVisible?: () => boolean;
  dir?: string;
  nodeId?: string;
}

const FloatingAddPluginMenu: React.FC<Props> = ({
  addPluginMenuConfig,
  onClick,
  supportedButtonIds,
  isVisible = () => true,
  dir,
  nodeId,
}) => {
  const floatingMenuWrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const modalService: ModalService = useContext(ModalServiceContext) || {};
  const { languageDir, isMobile } = useContext(RicosContext) || {};
  const editorQuery = useContext(EditorQueryContext);
  const { adapter } = useContext(EditorContext);
  const position = useSelectionToPosition(adapter.tiptapEditor);
  const { portal, toolbars } = useContext(ToolbarContext);
  const FloatingButtonCustomComponent = toolbars.pluginMenu.getVisibleFloatingButtonComponent({
    editorQuery: editorQuery.query,
    modalService,
    editor: adapter.tiptapEditor,
    nodeId,
  });

  const isHorizontalMenu =
    !isMobile && (!addPluginMenuConfig || addPluginMenuConfig?.horizontalMenuLayout);
  const layout: LayoutTypes = 'toolbar';
  const direction = dir || languageDir;

  const placement: Placement =
    direction === 'ltr'
      ? isHorizontalMenu
        ? 'right'
        : 'right-start'
      : isHorizontalMenu
      ? 'left'
      : 'left-start';

  const MODAL_ID = isHorizontalMenu ? PLUGIN_MENU_HORIZONTAL_MODAL_ID : PLUGIN_MENU_MODAL_ID;
  const isModalOpen = useModalState(getIsModalOpen(MODAL_ID));

  const calcButtonPosition = (position?: DOMRect): { top: string } => {
    const buttonHeight = 20;
    const { top = 0, height = 0 } = position || {};
    const topPosition = top + height / 2 - buttonHeight / 2;
    return { top: `${topPosition}px` };
  };

  const toggleAddPluginMenu = () => {
    const isModalOpen = () => modalService.isModalOpen(MODAL_ID);
    isModalOpen()
      ? modalService.closeModal(MODAL_ID)
      : modalService.openRegisteredModal(MODAL_ID, {
          positioning: { referenceElement: buttonRef?.current, placement },
          layout,
          componentProps: {
            referenceElement: buttonRef,
            supportedButtonIds,
          },
          onClickOutside(e: MouseEvent | TouchEvent) {
            const isToggleButton =
              e.target === buttonRef.current || buttonRef.current?.contains(e.target as Node);
            if (
              isToggleButton ||
              portal?.contains(document.activeElement) ||
              modalService.getOpenModals().length > 1
            ) {
              return;
            }

            // Click event on the button doesn't get triggered if we close modal at once
            setTimeout(() => modalService.closeModal(MODAL_ID), 0);
          },
        });
  };

  const onPlusButtonClick = () => (onClick ? onClick() : toggleAddPluginMenu());

  return !isMobile ? (
    <div
      className={classNames(styles.floatingAddPluginMenu_wrapper)}
      ref={floatingMenuWrapperRef}
      data-hook="floating-add-plugin-menu"
      dir={direction}
    >
      {!!position && FloatingButtonCustomComponent ? (
        <FloatingButtonCustomComponent
          className={classNames(
            styles.floatingAddPluginMenu_plus_button_position,
            styles[direction]
          )}
          position={calcButtonPosition(position)}
        />
      ) : !!position && isVisible() ? (
        <PlusButton
          onClick={onPlusButtonClick}
          position={calcButtonPosition(position)}
          ref={buttonRef}
          rotate={isModalOpen}
          direction={direction}
        />
      ) : null}
    </div>
  ) : null;
};

export default FloatingAddPluginMenu;
